import { signal, effect } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

/**
 * Verse bookmarks persisted in localStorage. Keyed by verse OSIS ref
 * (e.g. "Mat.1.21") so they work across translations. LRU-capped at 500
 * to keep the store from unbounded growth.
 */

export type Bookmark = {
  ref: string;
  bookId: string;
  bookName: string;
  chapter: number;
  verseNumber: number;
  text: string;
  createdAt: number;
};

const STORAGE_KEY = '_bm1';
const MAX_ENTRIES = 500;

function readStored(): Bookmark[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isBookmark);
  } catch {
    return [];
  }
}

function isBookmark(value: unknown): value is Bookmark {
  if (!value || typeof value !== 'object') return false;
  const b = value as Record<string, unknown>;
  return (
    typeof b.ref === 'string' &&
    typeof b.bookId === 'string' &&
    typeof b.bookName === 'string' &&
    typeof b.chapter === 'number' &&
    typeof b.verseNumber === 'number' &&
    typeof b.text === 'string' &&
    typeof b.createdAt === 'number'
  );
}

export const bookmarks = signal<Bookmark[]>(readStored());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarks.value));
  });
}

export function isBookmarked(ref: string): boolean {
  return bookmarks.value.some((b) => b.ref === ref);
}

export function addBookmark(entry: Omit<Bookmark, 'createdAt'>): void {
  const list = bookmarks.peek();
  if (list.some((b) => b.ref === entry.ref)) return;
  const next = [{ ...entry, createdAt: Date.now() }, ...list];
  bookmarks.value = next.slice(0, MAX_ENTRIES);
}

export function removeBookmark(ref: string): void {
  bookmarks.value = bookmarks.peek().filter((b) => b.ref !== ref);
}

export function toggleBookmark(entry: Omit<Bookmark, 'createdAt'>): boolean {
  if (isBookmarked(entry.ref)) {
    removeBookmark(entry.ref);
    return false;
  }
  addBookmark(entry);
  return true;
}
